import { SubscriptionPlan, SubscriptionStatus, FeatureKey } from '@numeo/types-core'
import axios from 'axios'
import {
    CustomerListResponse,
    SubscriptionOverview,
    CustomerDetail,
    QuotaHealthItem,
    UsageHistoryRow,
    TrialItem,
    CancelSubscriptionRequest,
    DeleteStripeCustomerRequest,
    ResetQuotaRequest,
    AdminActionResult,
    MigrateOrgSubscriptionResult,
    StripeProductItem,
    StripeProductDetail,
    CreateProductRequest,
    UpdateProductRequest,
    CreatePriceRequest,
    StripePriceItem,
    StripeCouponItem,
    CreateCouponRequest,
    InfluencerWithStats,
    InfluencerDetail,
    InfluencerProfile,
    CreateInfluencerRequest,
    UpdateInfluencerRequest,
    AssignMobilePlanRequest,
    AssignMobilePlanResult,
    MobileRevokeMode,
    RevokeMobilePlanResult,
    AssignPlanRequest,
    AssignPlanResult,
    ExtendTrialRequest,
    ExtendTrialResult,
    TmsGrantTrialRequest,
    TmsGrantTrialResult,
    TmsCancelResult,
} from './types'

interface ApiResponse<T> {
    success: boolean
    data: T
    message?: string
}

export const billingApi = {
    async getOverview(): Promise<SubscriptionOverview> {
        const response = await axios.get<ApiResponse<SubscriptionOverview>>('/gluon/billing/overview')
        return response.data.data
    },

    async getTrials(): Promise<TrialItem[]> {
        const response = await axios.get<ApiResponse<TrialItem[]>>('/gluon/billing/trials')
        return response.data.data
    },

    async getCustomers(params: {
        email?: string
        projectName?: string
        plan?: SubscriptionPlan
        status?: SubscriptionStatus
        page?: number
        pageSize?: number
    }): Promise<CustomerListResponse> {
        const response = await axios.get<ApiResponse<CustomerListResponse>>('/gluon/billing/customers', { params })
        return response.data.data
    },

    async getCustomerDetail(appId: string): Promise<CustomerDetail> {
        const response = await axios.get<ApiResponse<CustomerDetail>>(`/gluon/billing/customers/${appId}`)
        return response.data.data
    },

    async getQuotaHealth(appId: string): Promise<QuotaHealthItem[]> {
        const response = await axios.get<ApiResponse<QuotaHealthItem[]>>(`/gluon/billing/customers/${appId}/quota-health`)
        return response.data.data
    },

    async getUsageHistory(
        appId: string,
        params: {
            featureKey?: FeatureKey
            fromDate?: string
            toDate?: string
            page?: number
            pageSize?: number
        }
    ): Promise<{ rows: UsageHistoryRow[]; total: number }> {
        const response = await axios.get<ApiResponse<{ rows: UsageHistoryRow[]; total: number }>>(`/gluon/billing/customers/${appId}/usage-history`, { params })
        return response.data.data
    },

    /** Grant/extend/switch an extension trial (app owner by default, or a member via standalone userId). 409s over an active paid subscription. */
    async assignPlan(appId: string, data: AssignPlanRequest): Promise<AssignPlanResult> {
        const response = await axios.post<ApiResponse<AssignPlanResult>>(`/gluon/billing/customers/${appId}/assign-plan`, data)
        return response.data.data
    },

    /** Move an existing trial's end date. 409s over an active paid subscription. */
    async extendTrial(appId: string, data: ExtendTrialRequest): Promise<ExtendTrialResult> {
        const response = await axios.post<ApiResponse<ExtendTrialResult>>(`/gluon/billing/customers/${appId}/extend-trial`, data)
        return response.data.data
    },

    /** Grant (or extend) a TMS trial for the customer. 409s over an active paid TMS subscription. */
    async grantTmsTrial(appId: string, data: TmsGrantTrialRequest): Promise<TmsGrantTrialResult> {
        const response = await axios.post<ApiResponse<TmsGrantTrialResult>>(`/gluon/billing/customers/${appId}/tms/grant-trial`, data)
        return response.data.data
    },

    /** Cancel the customer's TMS subscription — trials end now, paid subs at period end. */
    async setTmsTrucks(appId: string, data: { trucks: number }): Promise<{ purchasedTrucks: number }> {
        const res = await axios.post(`/gluon/billing/customers/${appId}/tms/trucks`, data)
        return res.data.data
    },

    async cancelTms(appId: string, data: { reason?: string }): Promise<TmsCancelResult> {
        const response = await axios.post<ApiResponse<TmsCancelResult>>(`/gluon/billing/customers/${appId}/tms/cancel`, data)
        return response.data.data
    },

    async cancelSubscription(appId: string, data: CancelSubscriptionRequest): Promise<AdminActionResult> {
        const response = await axios.post<ApiResponse<AdminActionResult>>(`/gluon/billing/customers/${appId}/cancel-subscription`, data)
        return response.data.data
    },

    async deleteStripeCustomer(appId: string, data: DeleteStripeCustomerRequest): Promise<AdminActionResult> {
        const response = await axios.post<ApiResponse<AdminActionResult>>(`/gluon/billing/customers/${appId}/delete-stripe-customer`, data)
        return response.data.data
    },

    async resetQuota(appId: string, data: ResetQuotaRequest): Promise<AdminActionResult> {
        const response = await axios.post<ApiResponse<AdminActionResult>>(`/gluon/billing/customers/${appId}/reset-quota`, data)
        return response.data.data
    },

    /** Grant a user-scoped mobile tier as a fixed-length trial. */
    async assignMobilePlan(appId: string, userId: string, data: AssignMobilePlanRequest): Promise<AssignMobilePlanResult> {
        const response = await axios.post<ApiResponse<AssignMobilePlanResult>>(`/gluon/billing/customers/${appId}/users/${userId}/assign-mobile-plan`, data)
        return response.data.data
    },

    /** Revoke a mobile grant, immediately or at the end of the current period. */
    async revokeMobilePlan(appId: string, userId: string, mode: MobileRevokeMode): Promise<RevokeMobilePlanResult> {
        const response = await axios.post<ApiResponse<RevokeMobilePlanResult>>(`/gluon/billing/customers/${appId}/users/${userId}/revoke-mobile-plan`, {
            mode,
        })
        return response.data.data
    },

    async migrateToOrgSubscription(appId: string): Promise<MigrateOrgSubscriptionResult> {
        const response = await axios.post<ApiResponse<MigrateOrgSubscriptionResult>>(`/gluon/billing/customers/${appId}/migrate-to-org-subscription`)
        return response.data.data
    },

    async clearBillingCache(apiKey: string, applicationId?: string): Promise<{ success: boolean; message: string }> {
        const response = await axios.post<{ success: boolean; message: string }>(
            '/billing/admin/clear-cache',
            { applicationId: applicationId || undefined },
            { headers: { Authorization: `Bearer ${apiKey}` } }
        )
        return response.data
    },

    async getProducts(): Promise<StripeProductItem[]> {
        const response = await axios.get<ApiResponse<StripeProductItem[]>>('/gluon/billing/products')
        return response.data.data
    },

    async getProductDetail(productId: string): Promise<StripeProductDetail> {
        const response = await axios.get<ApiResponse<StripeProductDetail>>(`/gluon/billing/products/${productId}`)
        return response.data.data
    },

    async updateProductMetadata(productId: string, metadata: Record<string, string>, reason: string): Promise<StripeProductDetail> {
        const response = await axios.post<ApiResponse<StripeProductDetail>>(`/gluon/billing/products/${productId}/metadata`, { metadata, reason })
        return response.data.data
    },

    async createPaymentLink(priceId: string, reason: string): Promise<{ url: string }> {
        const response = await axios.post<ApiResponse<{ url: string }>>('/gluon/billing/products/payment-link', { priceId, reason })
        return response.data.data
    },

    async deactivateProduct(productId: string, reason: string): Promise<{ productId: string; deactivated: boolean }> {
        const response = await axios.post<ApiResponse<{ productId: string; deactivated: boolean }>>(`/gluon/billing/products/${productId}/deactivate`, {
            reason,
        })
        return response.data.data
    },

    async createProduct(data: CreateProductRequest): Promise<StripeProductDetail> {
        const response = await axios.post<ApiResponse<StripeProductDetail>>('/gluon/billing/products', data)
        return response.data.data
    },

    async updateProduct(productId: string, data: UpdateProductRequest): Promise<StripeProductDetail> {
        const response = await axios.put<ApiResponse<StripeProductDetail>>(`/gluon/billing/products/${productId}`, data)
        return response.data.data
    },

    async createPrice(productId: string, data: Omit<CreatePriceRequest, 'productId'>): Promise<StripePriceItem> {
        const response = await axios.post<ApiResponse<StripePriceItem>>(`/gluon/billing/products/${productId}/prices`, data)
        return response.data.data
    },

    async getCoupons(): Promise<StripeCouponItem[]> {
        const response = await axios.get<ApiResponse<StripeCouponItem[]>>('/gluon/billing/coupons')
        return response.data.data
    },

    async createCoupon(data: CreateCouponRequest): Promise<StripeCouponItem> {
        const response = await axios.post<ApiResponse<StripeCouponItem>>('/gluon/billing/coupons', data)
        return response.data.data
    },

    async deleteCoupon(couponId: string, reason: string): Promise<{ couponId: string; deleted: boolean }> {
        const response = await axios.delete<ApiResponse<{ couponId: string; deleted: boolean }>>(`/gluon/billing/coupons/${couponId}`, {
            data: { reason },
        })
        return response.data.data
    },

    // Full influencer data (profile + promo codes + stats) loaded once for the dashboard.
    async getInfluencers(): Promise<InfluencerDetail[]> {
        const response = await axios.get<ApiResponse<InfluencerDetail[]>>('/gluon/billing/influencers')
        return response.data.data
    },

    async createInfluencer(data: CreateInfluencerRequest): Promise<InfluencerProfile> {
        const response = await axios.post<ApiResponse<InfluencerProfile>>('/gluon/billing/influencers', data)
        return response.data.data
    },

    async updateInfluencer(id: string, data: UpdateInfluencerRequest): Promise<InfluencerWithStats> {
        const response = await axios.put<ApiResponse<InfluencerWithStats>>(`/gluon/billing/influencers/${id}`, data)
        return response.data.data
    },

    async deleteInfluencer(id: string): Promise<{ id: string; deleted: boolean }> {
        const response = await axios.delete<ApiResponse<{ id: string; deleted: boolean }>>(`/gluon/billing/influencers/${id}`)
        return response.data.data
    },
}
